import React, { useState, useEffect, createContext, useContext } from 'react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import Components
import LandingPage from './components/LandingPage';
import NaturalizationTest from './components/NaturalizationTest';
import NotificationSettings from './components/NotificationSettings';
import ProviderSearch from './components/ProviderSearch';
import ProviderProfile from './components/ProviderProfile';
import ResourcesCenter from './components/ResourcesCenter';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Fetch user profile after successful login
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(userResponse.data);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.response?.data?.detail
        || (error.response ? `Server error (${error.response.status})` : 'Cannot reach server — please try again.');
      return { success: false, error: msg };
    }
  };

  const register = async (email, password, full_name) => {
    try {
      await axios.post(`${API}/auth/register`, { email, password, full_name });
      return { success: true };
    } catch (error) {
      console.error('Register error:', error);
      const msg = error.response?.data?.detail
        || (error.response ? `Server error (${error.response.status})` : 'Cannot reach server — please try again.');
      return { success: false, error: msg };
    }
  };

  const googleLogin = async (accessToken) => {
    try {
      const response = await axios.post(`${API}/auth/google`, { access_token: accessToken });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(userResponse.data);
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      const msg = error.response?.data?.detail
        || (error.response ? `Server error (${error.response.status})` : 'Cannot reach server — please try again.');
      return { success: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear session
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear local storage and state
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    googleLogin,
    token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Header Component
const Header = () => {
  const { user, logout } = useAuth();

  return (
    <header className="bg-white border-b border-slate-200">
      <div className="container mx-auto flex justify-between items-center p-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-navy-700 rounded-lg flex items-center justify-center" aria-hidden="true"><svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
            <h1 className="text-2xl font-bold text-navy-700">My Immigration Services</h1>
          </div>
          {user && (
            <span className="bg-blue-50 px-3 py-1 rounded-full text-xs font-medium text-blue-700 border border-blue-200">
              {user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1)} Plan
            </span>
          )}
        </div>
        {user && (
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-slate-600">Welcome, {user.full_name}</span>
            <button
              onClick={logout}
              className="text-slate-600 hover:text-navy-700 hover:bg-slate-50 px-3 py-2 rounded-lg transition-colors text-sm font-medium"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

// Login Component
const Login = () => {
  const { login, googleLogin, user } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  // Check for existing session and OAuth callback
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Check for OAuth session_id in URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        await handleOAuthCallback(sessionId);
        return;
      }
      
      // Check for existing session
      try {
        const response = await axios.get(`${API}/auth/check-session`, {
          withCredentials: true
        });
        
        if (response.data.authenticated) {
          window.location.href = '/dashboard';
        }
      } catch (error) {
        console.log('No existing session');
      }
    };

    checkAuthStatus();
  }, []);

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (user) {
      window.location.href = '/dashboard';
    }
  }, [user]);

  const handleOAuthCallback = async (sessionId) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/oauth/session`, {
        session_id: sessionId
      }, {
        withCredentials: true
      });
      
      if (response.data.user) {
        // Clear the URL fragment
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Redirect to dashboard
        window.location.href = '/dashboard';
      }
    } catch (error) {
      setError('OAuth authentication failed. Please try again.');
      // Clear the URL fragment on error
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    setLoading(false);
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      const result = await googleLogin(tokenResponse.access_token);
      if (!result.success) {
        setError(result.error);
      }
      setLoading(false);
    },
    onError: () => setError('Google Sign-In was cancelled or failed'),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (showRegister) {
    return <Register onBack={() => setShowRegister(false)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-xl shadow-sm w-full max-w-md border border-slate-200">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-navy-700 rounded-lg flex items-center justify-center mx-auto" aria-hidden="true"><svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
          <h2 className="text-2xl font-bold text-navy-700">Login to My Immigration Services</h2>
          <p className="text-slate-500 text-sm mt-2">Track your immigration case status</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="login-email" className="block text-gray-700 text-sm font-bold mb-2">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>

          <div className="mb-6">
            <label htmlFor="login-password" className="block text-gray-700 text-sm font-bold mb-2">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors disabled:opacity-50 font-semibold"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        {/* OAuth Login Section */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500 font-medium">Or continue with</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={() => handleGoogleLogin()}
              disabled={loading || !GOOGLE_CLIENT_ID}
              className="w-full inline-flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => setShowRegister(true)}
            className="text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200"
          >
            Don't have an account? Register here
          </button>
        </div>
      </div>
    </div>
  );
};

// Register Component
const Register = ({ onBack }) => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');

    const result = await register(formData.email, formData.password, formData.full_name);
    
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-sm w-full max-w-md border border-slate-200">
          <div className="text-center">
            <div className="text-green-600 text-6xl mb-4" aria-hidden="true">✓</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Registration Successful!</h2>
            <p className="text-gray-600 mb-6">Your account has been created. You can now log in.</p>
            <button
              onClick={onBack}
              className="bg-navy-700 hover:bg-navy-800 text-white py-2 px-6 rounded-lg transition-colors font-semibold"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-xl shadow-sm w-full max-w-md border border-slate-200">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-navy-700 rounded-lg flex items-center justify-center mx-auto" aria-hidden="true"><svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
          <h2 className="text-2xl font-bold text-navy-700">Create Your Account</h2>
          <p className="text-slate-500 text-sm mt-2">Join My Immigration Services</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="register-full-name" className="block text-gray-700 text-sm font-bold mb-2">
              Full Name
            </label>
            <input
              id="register-full-name"
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="register-email" className="block text-gray-700 text-sm font-bold mb-2">
              Email
            </label>
            <input
              id="register-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="register-password" className="block text-gray-700 text-sm font-bold mb-2">
              Password
            </label>
            <input
              id="register-password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>

          <div className="mb-6">
            <label htmlFor="register-confirm-password" className="block text-gray-700 text-sm font-bold mb-2">
              Confirm Password
            </label>
            <input
              id="register-confirm-password"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors disabled:opacity-50 font-semibold"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            onClick={onBack}
            className="text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200"
          >
            Already have an account? Login here
          </button>
        </div>
      </div>
    </div>
  );
};

// Payment Success Component
const PaymentSuccess = () => {
  const { user } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdParam = urlParams.get('session_id');
    
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
      checkPaymentStatus(sessionIdParam);
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        // Refresh user data to show updated subscription
        window.location.href = '/dashboard';
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-xl shadow-sm w-full max-w-md text-center border border-slate-200">
        {paymentStatus === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Processing Payment...</h2>
            <p className="text-gray-600">Please wait while we verify your payment.</p>
          </>
        )}
        
        {paymentStatus === 'success' && (
          <>
            <div className="text-green-600 text-6xl mb-4" aria-hidden="true">✓</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">Your subscription has been upgraded successfully.</p>
            <a 
              href="/dashboard" 
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </a>
          </>
        )}
        
        {paymentStatus === 'error' && (
          <>
            <div className="text-red-600 text-6xl mb-4" aria-hidden="true">✗</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Error</h2>
            <p className="text-gray-600 mb-6">There was an error processing your payment.</p>
            <a 
              href="/dashboard" 
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </a>
          </>
        )}
        
        {paymentStatus === 'timeout' && (
          <>
            <div className="text-yellow-600 text-6xl mb-4" aria-hidden="true">⏰</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Verification Timeout</h2>
            <p className="text-gray-600 mb-6">Please check your email for payment confirmation or contact support.</p>
            <a 
              href="/dashboard" 
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </a>
          </>
        )}
      </div>
    </div>
  );
};

// Payment Cancel Component
const PaymentCancel = () => {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-xl shadow-sm w-full max-w-md text-center border border-slate-200">
        <div className="text-orange-600 text-6xl mb-4" aria-hidden="true">⚠</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Cancelled</h2>
        <p className="text-gray-600 mb-6">Your payment was cancelled. No charges were made to your account.</p>
        <a 
          href="/dashboard" 
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Dashboard
        </a>
      </div>
    </div>
  );
};

// Subscription Plans Component
const SubscriptionPlans = ({ onSelectPlan }) => {
  const { user, token } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      cases: 1,
      features: ['1 case tracking', 'Basic status updates']
    },
    {
      id: 'basic',
      name: 'Basic',
      price: 5,
      cases: 5,
      features: ['5 cases tracking', 'Email notifications', 'Status history']
    },
    {
      id: 'standard',
      name: 'Standard',
      price: 10,
      cases: 15,
      features: ['15 cases tracking', 'Email notifications', 'SMS notifications', 'Mobile app notifications']
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 29.99,
      cases: 'Unlimited',
      features: ['Unlimited cases', 'All notifications', 'Enterprise dashboard', 'Priority support']
    }
  ];

  const handleUpgrade = async (planId) => {
    if (planId === 'free' || user?.subscription_plan === planId) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payments/create-checkout`, {
        plan_id: planId,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.detail || 'Failed to create payment session');
    }

    setLoading(false);
  };

  return (
    <div className="py-8">
      <div className="text-center mb-8">
        <div className="w-12 h-12 bg-navy-700 rounded-lg flex items-center justify-center mx-auto" aria-hidden="true"><svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
        <h2 className="text-3xl font-bold text-navy-700 mb-2">Choose Your Plan</h2>
        <p className="text-slate-500 font-medium">Select the perfect subscription for your immigration needs</p>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6 font-semibold max-w-4xl mx-auto">
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto px-4">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white rounded-xl shadow-sm p-6 border ${
              user?.subscription_plan === plan.id
                ? 'border-blue-600 bg-blue-50/50 shadow-md'
                : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
            } transition-all duration-300 cursor-pointer`}
            onClick={() => onSelectPlan && onSelectPlan(plan.id)}
          >
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-navy-700">{plan.name}</h3>
              <div className="text-3xl font-bold text-navy-700 mt-2">
                ${plan.price}
                {plan.price > 0 && <span className="text-sm text-blue-600 font-semibold">/month</span>}
              </div>
            </div>
            
            <div className="mb-4">
              <div className="text-center text-lg font-bold text-navy-700 mb-2 bg-blue-50 py-2 rounded-lg border border-blue-200">
                {plan.cases} Case{plan.cases !== 1 && plan.cases !== 'Unlimited' ? 's' : ''}
              </div>
            </div>
            
            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-center text-sm text-navy-700 font-medium">
                  <span className="text-emerald-500 mr-2" aria-hidden="true">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            
            {user?.subscription_plan === plan.id ? (
              <div className="text-center">
                <div className="bg-emerald-50 text-emerald-700 font-semibold py-3 px-4 rounded-lg border border-emerald-200">
                  Current Plan
                </div>
              </div>
            ) : (
              <button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors disabled:opacity-50 font-semibold"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpgrade(plan.id);
                }}
              >
                {loading ? 'Processing...' : (
                  plan.price === 0
                    ? <>Get Started</>
                    : <><span aria-hidden="true">⬆️</span> Upgrade Now</>
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Case Management Component
const CaseManager = () => {
  const { token } = useAuth();
  const [cases, setCases] = useState([]);
  const [newCase, setNewCase] = useState({ case_number: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limits, setLimits] = useState({});

  useEffect(() => {
    fetchCases();
    fetchLimits();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    }
  };

  const fetchLimits = async () => {
    try {
      const response = await axios.get(`${API}/subscription/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    }
  };

  const addCase = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/cases`, newCase, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewCase({ case_number: '', description: '' });
      fetchCases();
      fetchLimits();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to add case');
    }
    
    setLoading(false);
  };

  const checkStatus = async (caseId) => {
    try {
      const response = await axios.post(`${API}/cases/${caseId}/check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        fetchCases(); // Refresh cases to show updated status
      } else {
        alert(`Failed to check status: ${response.data.error}`);
      }
    } catch (error) {
      alert('Failed to check status');
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const deleteCase = async (caseId) => {
    setShowDeleteConfirm(caseId);
  };

  const confirmDelete = async () => {
    if (showDeleteConfirm) {
      try {
        await axios.delete(`${API}/cases/${showDeleteConfirm}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCases();
        fetchLimits();
        setShowDeleteConfirm(null);
      } catch (error) {
        setError('Failed to delete case');
        setShowDeleteConfirm(null);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-navy-700 rounded-lg flex items-center justify-center" aria-hidden="true"><svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
            <h2 className="text-3xl font-bold text-navy-700">Your USCIS Cases</h2>
          </div>
          <div className="text-sm font-semibold">
            {limits.remaining !== undefined && limits.remaining >= 0 && (
              <span className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full border border-amber-200">
                {limits.remaining} of {limits.limit} cases remaining
              </span>
            )}
            {limits.remaining === -1 && (
              <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-200">
                <span aria-hidden="true">♾️</span> Unlimited cases
              </span>
            )}
          </div>
        </div>

        {/* Add New Case Form */}
        {limits.can_add && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-slate-200">
            <h3 className="text-xl font-bold text-navy-700 mb-4 flex items-center">
              <span className="mr-2" aria-hidden="true">📝</span>
              Add New Case
            </h3>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={addCase}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="case-number" className="block text-gray-700 text-sm font-bold mb-2">
                    USCIS Receipt Number
                  </label>
                  <input
                    id="case-number"
                    type="text"
                    value={newCase.case_number}
                    onChange={(e) => setNewCase({ ...newCase, case_number: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="EAC9999103403, MSC2190123456, IOE1234567890"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: ABC1234567890 (3 letters + 10 digits)
                  </p>
                </div>
                <div>
                  <label htmlFor="case-description" className="block text-gray-700 text-sm font-bold mb-2">
                    Description (Optional)
                  </label>
                  <input
                    id="case-description"
                    type="text"
                    value={newCase.description}
                    onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="e.g., My I-765 application"
                  />
                </div>
              </div>
              
              {/* Staging Receipt Numbers Helper */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <details>
                  <summary className="cursor-pointer font-semibold text-navy-700 hover:text-navy-700">
                    <span aria-hidden="true">📋</span> Test with USCIS Sandbox Receipt Numbers
                  </summary>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="font-medium text-blue-700 mb-1">With History:</p>
                      <div className="space-y-1">
                        {['EAC9999103403', 'EAC9999103404', 'LIN9999106498', 'SRC9999102777'].map(receipt => (
                          <button
                            key={receipt}
                            type="button"
                            onClick={() => setNewCase({ ...newCase, case_number: receipt })}
                            className="block text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded text-left w-full"
                          >
                            {receipt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-medium text-blue-700 mb-1">Without History:</p>
                      <div className="space-y-1">
                        {['EAC9999103400', 'EAC9999103402', 'LIN9999106501', 'SRC9999132694'].map(receipt => (
                          <button
                            key={receipt}
                            type="button"
                            onClick={() => setNewCase({ ...newCase, case_number: receipt })}
                            className="block text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded text-left w-full"
                          >
                            {receipt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600">
                      <p className="font-medium"><span aria-hidden="true">ℹ️</span> Testing Notes:</p>
                      <p>• These are USCIS sandbox receipt numbers</p>
                      <p>• "With History" receipts have case timeline data</p>
                      <p>• Click any number to fill the form</p>
                    </div>
                  </div>
                </details>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors disabled:opacity-50 font-semibold"
              >
                {loading
                  ? <><span aria-hidden="true">⏳</span> Adding...</>
                  : <><span aria-hidden="true">➕</span> Add Case</>}
              </button>
            </form>
          </div>
        )}

        {!limits.can_add && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 px-6 py-4 rounded-xl mb-6 font-medium text-center">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl" aria-hidden="true">⚠️</span>
              <span>Case limit reached. Upgrade your subscription to add more cases.</span>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 border border-slate-200">
              <div className="text-center">
                <div className="text-red-600 text-4xl mb-4" aria-hidden="true">⚠️</div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Case</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this case? This action cannot be undone.</p>
                <div className="flex space-x-4">
                  <button
                    onClick={cancelDelete}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cases List */}
        <div className="space-y-4">
          {cases.length === 0 ? (
            <div className="bg-slate-50 rounded-xl p-8 text-center border border-slate-200">
              <div className="w-16 h-16 bg-navy-700 rounded-xl flex items-center justify-center mx-auto mb-4" aria-hidden="true"><svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418" /></svg></div>
              <p className="text-navy-700 font-semibold text-lg">No cases added yet. Add your first case above!</p>
            </div>
          ) : (
            cases.map((caseItem) => (
              <div key={caseItem.id} className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 hover:border-slate-300 transition-all duration-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-semibold">{caseItem.case_number}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        caseItem.current_status 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {caseItem.current_status || 'No status checked'}
                      </span>
                    </div>
                    
                    {caseItem.description && (
                      <p className="text-gray-600 mb-2">{caseItem.description}</p>
                    )}
                    
                    <div className="text-sm text-gray-500">
                      <div>Added: {new Date(caseItem.created_at).toLocaleDateString()}</div>
                      {caseItem.last_checked && (
                        <div>Last checked: {new Date(caseItem.last_checked).toLocaleString()}</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => checkStatus(caseItem.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-semibold"
                    >
                      <span aria-hidden="true">🔍</span> Check Status
                    </button>
                    <button
                      onClick={() => deleteCase(caseItem.id)}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-semibold"
                    >
                      <span aria-hidden="true">🗑️</span> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [usersResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
      ]);

      setUsers(usersResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const updateUserSubscription = async (userId, plan) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/subscription`, { plan }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      fetchAdminData(); // Refresh data
      showNotification('Subscription updated successfully', 'success');
    } catch (error) {
      showNotification('Failed to update subscription');
    }
  };

  const [showUserDeleteConfirm, setShowUserDeleteConfirm] = useState(null);

  const deleteUser = async (userId) => {
    setShowUserDeleteConfirm(userId);
  };

  const confirmUserDelete = async () => {
    if (showUserDeleteConfirm) {
      try {
        await axios.delete(`${API}/admin/users/${showUserDeleteConfirm}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        
        fetchAdminData(); // Refresh data
        setShowUserDeleteConfirm(null);
      } catch (error) {
        showNotification('Failed to delete user');
        setShowUserDeleteConfirm(null);
      }
    }
  };

  const cancelUserDelete = () => {
    setShowUserDeleteConfirm(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Notification Component */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            notification.type === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <div className="flex items-center">
              <span className="mr-2" aria-hidden="true">
                {notification.type === 'success' ? '✅' : '❌'}
              </span>
              {notification.message}
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3 mb-8">
          <div className="text-3xl" aria-hidden="true">👑</div>
          <h1 className="text-3xl font-bold text-navy-700">Admin Dashboard</h1>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Users</p>
                <p className="text-2xl font-bold text-navy-700">{stats.total_users || 0}</p>
              </div>
              <div className="text-blue-500 text-2xl" aria-hidden="true">👥</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Total Cases</p>
                <p className="text-2xl font-bold text-red-700">{stats.total_cases || 0}</p>
              </div>
              <div className="text-red-500 text-2xl" aria-hidden="true">📋</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-emerald-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Active Sessions</p>
                <p className="text-2xl font-bold text-green-700">{stats.active_sessions || 0}</p>
              </div>
              <div className="text-green-500 text-2xl" aria-hidden="true">🔐</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Revenue</p>
                <p className="text-2xl font-bold text-slate-700">
                  ${((stats.subscription_breakdown?.basic || 0) * 5 +
                     (stats.subscription_breakdown?.standard || 0) * 10 +
                     (stats.subscription_breakdown?.enterprise || 0) * 29.99).toFixed(2)}
                </p>
              </div>
              <div className="text-slate-500 text-2xl" aria-hidden="true">💰</div>
            </div>
          </div>
        </div>

        {/* User Delete Confirmation Modal */}
        {showUserDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 border border-slate-200">
              <div className="text-center">
                <div className="text-red-600 text-4xl mb-4" aria-hidden="true">⚠️</div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Delete User</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this user? This action cannot be undone and will remove all their cases and data.</p>
                <div className="flex space-x-4">
                  <button
                    onClick={cancelUserDelete}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmUserDelete}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-navy-700">User Management</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Subscription</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Auth Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.profile_picture ? (
                          <img className="h-10 w-10 rounded-full" src={user.profile_picture} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.full_name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            {user.full_name}
                            {user.is_admin && (
                              <>
                                <span className="ml-2 text-yellow-500" aria-hidden="true">👑</span>
                                <span className="sr-only"> (Admin)</span>
                              </>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        id={`admin-user-subscription-${user.id}`}
                        aria-label={`Subscription plan for ${user.full_name}`}
                        value={user.subscription_plan}
                        onChange={(e) => updateUserSubscription(user.id, e.target.value)}
                        className="text-sm border rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        disabled={user.is_admin}
                      >
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="standard">Standard</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.oauth_provider ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          OAuth ({user.oauth_provider})
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-navy-700">
                          Email/Password
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!user.is_admin && (
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Legal Marketplace Dashboards (Phase M3)
// ─────────────────────────────────────────────────────────────

const PROVIDER_PRACTICE_AREAS = [
  { value: 'asylum', label: 'Asylum' },
  { value: 'family', label: 'Family-Based' },
  { value: 'employment', label: 'Employment-Based' },
  { value: 'deportation', label: 'Deportation Defense' },
  { value: 'naturalization', label: 'Naturalization' },
  { value: 'other', label: 'Other' },
];

const PROVIDER_LANGUAGES = [
  'English', 'Spanish', 'French', 'Mandarin', 'Cantonese', 'Vietnamese',
  'Tagalog', 'Arabic', 'Portuguese', 'Russian', 'Korean', 'Haitian Creole',
  'Amharic', 'Somali', 'Hindi', 'Punjabi', 'Urdu', 'Farsi/Persian',
];

const PROVIDER_BIO_MAX_WORDS = 200;

const emptyProviderForm = {
  provider_type: 'attorney',
  display_name: '',
  bio: '',
  photo_url: '',
  bar_number: '',
  states_licensed: '',
  practice_areas: [],
  languages: [],
  email: '',
  phone: '',
  website: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  consultation_type: 'free',
  consultation_mode: 'virtual',
};

// Converts a provider object from the API (arrays) into form state (states_licensed as CSV string).
const providerToForm = (provider) => ({
  provider_type: provider.provider_type || 'attorney',
  display_name: provider.display_name || '',
  bio: provider.bio || '',
  photo_url: provider.photo_url || '',
  bar_number: provider.bar_number || '',
  states_licensed: (provider.states_licensed || []).join(', '),
  practice_areas: provider.practice_areas || [],
  languages: provider.languages || [],
  email: provider.email || '',
  phone: provider.phone || '',
  website: provider.website || '',
  address: provider.address || '',
  city: provider.city || '',
  state: provider.state || '',
  zip: provider.zip || '',
  consultation_type: provider.consultation_type || 'free',
  consultation_mode: provider.consultation_mode || 'virtual',
});

// Builds the API payload from form state (states_licensed CSV -> array).
const formToProviderPayload = (form) => ({
  ...form,
  states_licensed: form.states_licensed
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean),
});

const ProviderDashboard = () => {
  const { token } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyProviderForm);
  const [inquiries, setInquiries] = useState([]);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchProvider = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/providers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setProvider(res.data);
        setForm(providerToForm(res.data));
        fetchInquiries();
      } else {
        setProvider(null);
        setForm(emptyProviderForm);
      }
    } catch (error) {
      console.error('Failed to fetch provider listing:', error);
      showNotification('Failed to load your listing', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchInquiries = async () => {
    try {
      const res = await axios.get(`${API}/providers/me/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(res.data || []);
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    }
  };

  useEffect(() => {
    fetchProvider();
  }, []);

  const bioWordCount = form.bio.trim() ? form.bio.trim().split(/\s+/).length : 0;
  const bioOverLimit = bioWordCount > PROVIDER_BIO_MAX_WORDS;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const togglePracticeArea = (value) => {
    setForm((prev) => ({
      ...prev,
      practice_areas: prev.practice_areas.includes(value)
        ? prev.practice_areas.filter((v) => v !== value)
        : [...prev.practice_areas, value],
    }));
  };

  const toggleLanguage = (value) => {
    setForm((prev) => ({
      ...prev,
      languages: prev.languages.includes(value)
        ? prev.languages.filter((v) => v !== value)
        : [...prev.languages, value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (bioOverLimit) return;

    setSaving(true);
    try {
      const payload = formToProviderPayload(form);
      if (provider) {
        const res = await axios.put(`${API}/providers/${provider.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProvider(res.data);
        setForm(providerToForm(res.data));
        showNotification('Listing updated successfully', 'success');
      } else {
        const res = await axios.post(`${API}/providers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProvider(res.data);
        setForm(providerToForm(res.data));
        showNotification('Listing created successfully', 'success');
        fetchInquiries();
      }
    } catch (error) {
      console.error('Failed to save provider listing:', error);
      showNotification(error.response?.data?.detail || 'Failed to save your listing', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your listing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            notification.type === 'success'
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <div className="flex items-center">
              <span className="mr-2" aria-hidden="true">{notification.type === 'success' ? '✅' : '❌'}</span>
              {notification.message}
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3 mb-6">
          <div className="text-3xl" aria-hidden="true">⚖️</div>
          <h2 className="text-3xl font-bold text-navy-700">My Listing</h2>
        </div>

        {!provider && (
          <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200 text-navy-700 font-semibold">
            You don't have a legal marketplace listing yet. Fill out the form below to create one.
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="provider-type" className="block text-gray-700 text-sm font-bold mb-2">Provider Type</label>
                <select
                  id="provider-type"
                  name="provider_type"
                  value={form.provider_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                >
                  <option value="attorney">Attorney</option>
                  <option value="firm">Law Firm</option>
                </select>
              </div>

              <div>
                <label htmlFor="provider-display-name" className="block text-gray-700 text-sm font-bold mb-2">Display Name</label>
                <input
                  id="provider-display-name"
                  type="text"
                  name="display_name"
                  value={form.display_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="provider-bio" className="block text-gray-700 text-sm font-bold mb-2">Bio</label>
              <textarea
                name="bio"
                value={form.bio}
                onChange={handleChange}
                rows={5}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                  bioOverLimit ? 'border-red-600 focus:border-red-600' : 'focus:border-blue-500'
                }`}
                id="provider-bio"
                placeholder="Tell prospective clients about your practice (max 200 words)..."
                required
              />
              <p className={`text-xs mt-1 font-semibold ${bioOverLimit ? 'text-red-600' : 'text-gray-500'}`}>
                {bioWordCount} / {PROVIDER_BIO_MAX_WORDS} words
                {bioOverLimit && ' — please shorten your bio before submitting'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="provider-photo-url" className="block text-gray-700 text-sm font-bold mb-2">Photo URL</label>
                <input
                  id="provider-photo-url"
                  type="text"
                  name="photo_url"
                  value={form.photo_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label htmlFor="provider-bar-number" className="block text-gray-700 text-sm font-bold mb-2">Bar Number</label>
                <input
                  id="provider-bar-number"
                  type="text"
                  name="bar_number"
                  value={form.bar_number}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="provider-states-licensed" className="block text-gray-700 text-sm font-bold mb-2">States Licensed</label>
              <input
                id="provider-states-licensed"
                type="text"
                name="states_licensed"
                value={form.states_licensed}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="e.g. CA, NY, TX"
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated list of state abbreviations</p>
            </div>

            <fieldset className="mt-4 border-0 p-0 m-0 min-w-0">
              <legend className="block text-gray-700 text-sm font-bold mb-2 p-0">Practice Areas</legend>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PROVIDER_PRACTICE_AREAS.map((pa) => (
                  <label key={pa.value} className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.practice_areas.includes(pa.value)}
                      onChange={() => togglePracticeArea(pa.value)}
                      className="rounded border-gray-300"
                    />
                    <span>{pa.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-4 border-0 p-0 m-0 min-w-0">
              <legend className="block text-gray-700 text-sm font-bold mb-2 p-0">Languages Spoken</legend>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto pr-1">
                {PROVIDER_LANGUAGES.map((lang) => (
                  <label key={lang} className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.languages.includes(lang)}
                      onChange={() => toggleLanguage(lang)}
                      className="rounded border-gray-300"
                    />
                    <span>{lang}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="provider-email" className="block text-gray-700 text-sm font-bold mb-2">Contact Email</label>
                <input
                  id="provider-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>
              <div>
                <label htmlFor="provider-phone" className="block text-gray-700 text-sm font-bold mb-2">Phone</label>
                <input
                  id="provider-phone"
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="provider-website" className="block text-gray-700 text-sm font-bold mb-2">Website</label>
              <input
                id="provider-website"
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="https://..."
              />
            </div>

            <div className="mt-4">
              <label htmlFor="provider-address" className="block text-gray-700 text-sm font-bold mb-2">Address</label>
              <input
                id="provider-address"
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label htmlFor="provider-city" className="block text-gray-700 text-sm font-bold mb-2">City</label>
                <input
                  id="provider-city"
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>
              <div>
                <label htmlFor="provider-state" className="block text-gray-700 text-sm font-bold mb-2">State</label>
                <input
                  id="provider-state"
                  type="text"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>
              <div>
                <label htmlFor="provider-zip" className="block text-gray-700 text-sm font-bold mb-2">ZIP</label>
                <input
                  id="provider-zip"
                  type="text"
                  name="zip"
                  value={form.zip}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="provider-consultation-type" className="block text-gray-700 text-sm font-bold mb-2">Consultation Type</label>
                <select
                  id="provider-consultation-type"
                  name="consultation_type"
                  value={form.consultation_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <label htmlFor="provider-consultation-mode" className="block text-gray-700 text-sm font-bold mb-2">Consultation Mode</label>
                <select
                  id="provider-consultation-mode"
                  name="consultation_mode"
                  value={form.consultation_mode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="virtual">Virtual</option>
                  <option value="in-person">In-Person</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={saving || bioOverLimit}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors disabled:opacity-50 font-semibold"
            >
              {saving
                ? <><span aria-hidden="true">⏳</span> Saving...</>
                : provider
                  ? <><span aria-hidden="true">💾</span> Update Listing</>
                  : <><span aria-hidden="true">➕</span> Create Listing</>}
            </button>
          </form>
        </div>

        {provider && (
          <div className="mt-8">
            <h3 className="text-2xl font-bold text-navy-700 mb-4 flex items-center">
              <span className="mr-2" aria-hidden="true">📥</span>
              Inquiries ({inquiries.length})
            </h3>
            {inquiries.length === 0 ? (
              <div className="bg-slate-50 rounded-xl p-8 text-center border border-slate-200">
                <p className="text-navy-700 font-semibold">No inquiries yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inq) => (
                  <div key={inq.id} className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <div>
                        <p className="font-semibold text-gray-900">{inq.from_name}</p>
                        <a href={`mailto:${inq.from_email}`} className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                          {inq.from_email}
                        </a>
                      </div>
                      <span className="text-xs text-gray-500">
                        {inq.created_at ? new Date(inq.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <p className="text-gray-700 mt-3 whitespace-pre-line">{inq.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin "Providers" Tab Component
const AdminProvidersTab = () => {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { providerId, action: 'verify' | 'deactivate', displayName }

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(res.data || []);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      showNotification('Failed to load providers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const requestAction = (providerId, action, displayName) => {
    setConfirmAction({ providerId, action, displayName });
  };

  const cancelAction = () => setConfirmAction(null);

  const confirmActionRun = async () => {
    if (!confirmAction) return;
    const { providerId, action } = confirmAction;
    try {
      await axios.put(`${API}/admin/providers/${providerId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification(`Provider ${action === 'verify' ? 'verified' : 'deactivated'} successfully`, 'success');
      fetchProviders();
    } catch (error) {
      console.error(`Failed to ${action} provider:`, error);
      showNotification(`Failed to ${action} provider`, 'error');
    } finally {
      setConfirmAction(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading providers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            notification.type === 'success'
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <div className="flex items-center">
              <span className="mr-2" aria-hidden="true">{notification.type === 'success' ? '✅' : '❌'}</span>
              {notification.message}
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3 mb-8">
          <div className="text-3xl" aria-hidden="true">⚖️</div>
          <h1 className="text-3xl font-bold text-navy-700">Legal Marketplace Providers</h1>
        </div>

        {confirmAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 border border-slate-200">
              <div className="text-center">
                <div className="text-blue-600 text-4xl mb-4" aria-hidden="true">
                  {confirmAction.action === 'verify' ? '✓' : '⚠️'}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {confirmAction.action === 'verify' ? 'Verify Provider' : 'Deactivate Provider'}
                </h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to {confirmAction.action} "{confirmAction.displayName}"?
                  {confirmAction.action === 'deactivate' && ' This will hide the listing from public search.'}
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={cancelAction}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmActionRun}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-navy-700">Provider Listings ({providers.length})</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Contact Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Verified</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Active</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {providers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      No provider listings yet.
                    </td>
                  </tr>
                ) : (
                  providers.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {p.display_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                        {p.provider_type === 'firm' ? 'Law Firm' : 'Attorney'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {[p.city, p.state].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {p.email || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {p.is_verified ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
                            <span aria-hidden="true">✓</span> Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            Unverified
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {p.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-navy-700">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                        {!p.is_verified && (
                          <button
                            onClick={() => requestAction(p.id, 'verify', p.display_name)}
                            className="text-green-700 hover:text-green-900 font-semibold"
                          >
                            Verify
                          </button>
                        )}
                        {p.is_active && (
                          <button
                            onClick={() => requestAction(p.id, 'deactivate', p.display_name)}
                            className="text-red-600 hover:text-red-900 font-semibold"
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Immigration Resources Center — Admin Tab (M2)
// ─────────────────────────────────────────────────────────────

const emptyResourceCategoryForm = {
  name: '', description: '', icon: '', display_order: 0, is_active: true,
};

const emptyResourceForm = {
  category_id: '', title: '', description: '', agency: '', url: '', icon: '',
  is_official: false, is_featured: false, is_active: true, display_order: 0,
};

const AdminResourcesTab = () => {
  const { token } = useAuth();
  const [subTab, setSubTab] = useState('categories'); // 'categories' | 'resources' | 'analytics'

  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [analytics, setAnalytics] = useState({ total_clicks: 0, resources: [] });
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  };

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/resource-categories`, authHeader);
      setCategories(res.data || []);
    } catch (error) {
      console.error('Failed to fetch resource categories:', error);
      showNotification('Failed to load categories', 'error');
    }
  };

  // Full admin directory — includes inactive resources with their complete
  // records, so they're fully editable (no more partial/reactivate-to-edit
  // workaround).
  const fetchResources = async () => {
    try {
      const res = await axios.get(`${API}/admin/resources`, authHeader);
      setResources(res.data || []);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      showNotification('Failed to load resources', 'error');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/resources/analytics`, authHeader);
      setAnalytics(res.data || { total_clicks: 0, resources: [] });
    } catch (error) {
      console.error('Failed to fetch resource analytics:', error);
      showNotification('Failed to load analytics', 'error');
    }
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([fetchCategories(), fetchResources(), fetchAnalytics()]);
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  // ---------- Categories ----------
  const [categoryForm, setCategoryForm] = useState(emptyResourceCategoryForm);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const startCreateCategory = () => {
    setCategoryForm(emptyResourceCategoryForm);
    setEditingCategoryId(null);
    setShowCategoryForm(true);
  };

  const startEditCategory = (cat) => {
    setCategoryForm({
      name: cat.name || '', description: cat.description || '', icon: cat.icon || '',
      display_order: cat.display_order || 0, is_active: cat.is_active !== false,
    });
    setEditingCategoryId(cat.id);
    setShowCategoryForm(true);
  };

  const cancelCategoryForm = () => {
    setShowCategoryForm(false);
    setEditingCategoryId(null);
    setCategoryForm(emptyResourceCategoryForm);
  };

  const handleCategoryFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitCategoryForm = async (e) => {
    e.preventDefault();
    const payload = {
      name: categoryForm.name,
      description: categoryForm.description || null,
      icon: categoryForm.icon,
      display_order: Number(categoryForm.display_order) || 0,
      is_active: categoryForm.is_active,
    };
    try {
      if (editingCategoryId) {
        await axios.put(`${API}/admin/resource-categories/${editingCategoryId}`, payload, authHeader);
        showNotification('Category updated', 'success');
      } else {
        await axios.post(`${API}/admin/resource-categories`, payload, authHeader);
        showNotification('Category created', 'success');
      }
      cancelCategoryForm();
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      showNotification(error.response?.data?.detail || 'Failed to save category', 'error');
    }
  };

  const moveCategory = async (categoryId, direction) => {
    const ordered = [...categories].sort((a, b) => a.display_order - b.display_order);
    const idx = ordered.findIndex((c) => c.id === categoryId);
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (idx === -1 || swapWith < 0 || swapWith >= ordered.length) return;

    [ordered[idx], ordered[swapWith]] = [ordered[swapWith], ordered[idx]];
    const reordered = ordered.map((c, i) => ({ id: c.id, display_order: i }));

    // Optimistic local update
    setCategories((prev) =>
      prev.map((c) => {
        const match = reordered.find((r) => r.id === c.id);
        return match ? { ...c, display_order: match.display_order } : c;
      })
    );

    try {
      await axios.put(`${API}/admin/resource-categories/reorder`, reordered, authHeader);
    } catch (error) {
      console.error('Failed to reorder categories:', error);
      showNotification('Failed to reorder categories', 'error');
      fetchCategories();
    }
  };

  // ---------- Resources ----------
  const [resourceForm, setResourceForm] = useState(emptyResourceForm);
  const [editingResourceId, setEditingResourceId] = useState(null);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [resourceSearch, setResourceSearch] = useState('');
  const [resourceCategoryFilter, setResourceCategoryFilter] = useState('');

  const startCreateResource = () => {
    setResourceForm(emptyResourceForm);
    setEditingResourceId(null);
    setShowResourceForm(true);
  };

  const startEditResource = (res) => {
    setResourceForm({
      category_id: res.category_id || '',
      title: res.title || '',
      description: res.description || '',
      agency: res.agency || '',
      url: res.url || '',
      icon: res.icon || '',
      is_official: !!res.is_official,
      is_featured: !!res.is_featured,
      is_active: res.is_active !== false,
      display_order: res.display_order || 0,
    });
    setEditingResourceId(res.id);
    setShowResourceForm(true);
  };

  const cancelResourceForm = () => {
    setShowResourceForm(false);
    setEditingResourceId(null);
    setResourceForm(emptyResourceForm);
  };

  const handleResourceFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setResourceForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitResourceForm = async (e) => {
    e.preventDefault();
    const payload = {
      category_id: resourceForm.category_id,
      title: resourceForm.title,
      description: resourceForm.description || null,
      agency: resourceForm.agency || null,
      url: resourceForm.url,
      icon: resourceForm.icon || null,
      is_official: resourceForm.is_official,
      is_featured: resourceForm.is_featured,
      is_active: resourceForm.is_active,
      display_order: Number(resourceForm.display_order) || 0,
    };
    try {
      if (editingResourceId) {
        await axios.put(`${API}/admin/resources/${editingResourceId}`, payload, authHeader);
        showNotification('Resource updated', 'success');
      } else {
        await axios.post(`${API}/admin/resources`, payload, authHeader);
        showNotification('Resource created', 'success');
      }
      cancelResourceForm();
      fetchResources();
    } catch (error) {
      console.error('Failed to save resource:', error);
      showNotification(error.response?.data?.detail || 'Failed to save resource', 'error');
    }
  };

  const toggleResourceField = async (res, field) => {
    const nextValue = !res[field];
    // Optimistic local update
    setResources((prev) => prev.map((r) => (r.id === res.id ? { ...r, [field]: nextValue } : r)));
    try {
      await axios.put(`${API}/admin/resources/${res.id}`, { [field]: nextValue }, authHeader);
      showNotification('Resource updated', 'success');
    } catch (error) {
      console.error(`Failed to toggle ${field}:`, error);
      showNotification('Failed to update resource', 'error');
      fetchResources();
    }
  };

  // ---------- Delete confirmation (shared, in-page modal) ----------
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'category'|'resource', id, label }

  const requestDelete = (type, id, label) => setConfirmDelete({ type, id, label });
  const cancelDelete = () => setConfirmDelete(null);

  const runDelete = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    try {
      if (type === 'category') {
        await axios.delete(`${API}/admin/resource-categories/${id}`, authHeader);
        showNotification('Category deleted', 'success');
        fetchCategories();
      } else {
        await axios.delete(`${API}/admin/resources/${id}`, authHeader);
        showNotification('Resource deleted', 'success');
        fetchResources();
      }
    } catch (error) {
      console.error(`Failed to delete ${type}:`, error);
      showNotification(error.response?.data?.detail || `Failed to delete ${type}`, 'error');
    } finally {
      setConfirmDelete(null);
    }
  };

  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const sortedCategories = [...categories].sort((a, b) => a.display_order - b.display_order);

  const filteredResources = resources
    .filter((r) => !resourceCategoryFilter || r.category_id === resourceCategoryFilter)
    .filter((r) => !resourceSearch || r.title.toLowerCase().includes(resourceSearch.toLowerCase()))
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            notification.type === 'success'
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <div className="flex items-center">
              <span className="mr-2" aria-hidden="true">{notification.type === 'success' ? '✅' : '❌'}</span>
              {notification.message}
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3 mb-6">
          <div className="text-3xl" aria-hidden="true">📚</div>
          <h1 className="text-3xl font-bold text-navy-700">Immigration Resources Center</h1>
        </div>

        {/* Sub-tabs */}
        <div className="flex space-x-2 mb-6 border-b border-slate-200">
          {[
            { id: 'categories', label: 'Categories' },
            { id: 'resources', label: 'Resources' },
            { id: 'analytics', label: 'Analytics' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`py-2 px-4 font-semibold text-sm rounded-t-lg transition-colors ${
                subTab === t.id
                  ? 'bg-blue-900 text-white'
                  : 'bg-blue-50 text-navy-700 hover:bg-blue-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Delete confirmation modal */}
        {confirmDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 border border-slate-200">
              <div className="text-center">
                <div className="text-red-600 text-4xl mb-4" aria-hidden="true">⚠️</div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Delete {confirmDelete.type === 'category' ? 'Category' : 'Resource'}
                </h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete "{confirmDelete.label}"? This action cannot be undone.
                </p>
                <div className="flex space-x-4">
                  <button onClick={cancelDelete} className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors font-semibold">
                    Cancel
                  </button>
                  <button onClick={runDelete} className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-semibold">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- Categories ---------------- */}
        {subTab === 'categories' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy-700">Categories</h2>
              <button
                onClick={startCreateCategory}
                className="bg-blue-800 hover:bg-blue-900 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                + Add Category
              </button>
            </div>

            {showCategoryForm && (
              <form onSubmit={submitCategoryForm} className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 space-y-4">
                <h3 className="text-lg font-bold text-navy-700">{editingCategoryId ? 'Edit Category' : 'New Category'}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="cat-name" className="block text-gray-700 text-sm font-bold mb-1">Name</label>
                    <input id="cat-name" name="name" value={categoryForm.name} onChange={handleCategoryFormChange} required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label htmlFor="cat-icon" className="block text-gray-700 text-sm font-bold mb-1">Icon (emoji)</label>
                    <input id="cat-icon" name="icon" value={categoryForm.icon} onChange={handleCategoryFormChange} required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cat-description" className="block text-gray-700 text-sm font-bold mb-1">Description</label>
                    <textarea id="cat-description" name="description" value={categoryForm.description} onChange={handleCategoryFormChange} rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label htmlFor="cat-display-order" className="block text-gray-700 text-sm font-bold mb-1">Display Order</label>
                    <input id="cat-display-order" name="display_order" type="number" value={categoryForm.display_order} onChange={handleCategoryFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div className="flex items-center">
                    <input id="cat-is-active" name="is_active" type="checkbox" checked={categoryForm.is_active} onChange={handleCategoryFormChange}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 mr-2" />
                    <label htmlFor="cat-is-active" className="text-gray-700 font-medium">Active</label>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
                    {editingCategoryId ? 'Save Changes' : 'Create Category'}
                  </button>
                  <button type="button" onClick={cancelCategoryForm} className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-semibold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Icon</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Slug</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Active</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-navy-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedCategories.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No categories yet.</td></tr>
                  ) : (
                    sortedCategories.map((cat, i) => (
                      <tr key={cat.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => moveCategory(cat.id, 'up')}
                              disabled={i === 0}
                              aria-label={`Move ${cat.name} up`}
                              className="text-blue-700 hover:text-navy-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            >▲</button>
                            <button
                              onClick={() => moveCategory(cat.id, 'down')}
                              disabled={i === sortedCategories.length - 1}
                              aria-label={`Move ${cat.name} down`}
                              className="text-blue-700 hover:text-navy-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            >▼</button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xl" aria-hidden="true">{cat.icon}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{cat.name}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{cat.slug}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {cat.is_active ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-navy-700">Active</span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-3">
                          <button onClick={() => startEditCategory(cat)} className="text-blue-700 hover:text-navy-700 font-semibold">Edit</button>
                          <button onClick={() => requestDelete('category', cat.id, cat.name)} className="text-red-600 hover:text-red-900 font-semibold">Delete</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ---------------- Resources ---------------- */}
        {subTab === 'resources' && (
          <div className="space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-3">
              <h2 className="text-xl font-bold text-navy-700">Resources</h2>
              <button
                onClick={startCreateResource}
                className="bg-blue-800 hover:bg-blue-900 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                + Add Resource
              </button>
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <label htmlFor="res-filter-search" className="sr-only">Search resources</label>
                <input
                  id="res-filter-search"
                  type="text"
                  placeholder="Search by title…"
                  value={resourceSearch}
                  onChange={(e) => setResourceSearch(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div>
                <label htmlFor="res-filter-category" className="sr-only">Filter by category</label>
                <select
                  id="res-filter-category"
                  value={resourceCategoryFilter}
                  onChange={(e) => setResourceCategoryFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">All Categories</option>
                  {sortedCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {showResourceForm && (
              <form onSubmit={submitResourceForm} className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 space-y-4">
                <h3 className="text-lg font-bold text-navy-700">{editingResourceId ? 'Edit Resource' : 'New Resource'}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="res-category" className="block text-gray-700 text-sm font-bold mb-1">Category</label>
                    <select id="res-category" name="category_id" value={resourceForm.category_id} onChange={handleResourceFormChange} required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20">
                      <option value="">Select a category…</option>
                      {sortedCategories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="res-title" className="block text-gray-700 text-sm font-bold mb-1">Title</label>
                    <input id="res-title" name="title" value={resourceForm.title} onChange={handleResourceFormChange} required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="res-description" className="block text-gray-700 text-sm font-bold mb-1">Description</label>
                    <textarea id="res-description" name="description" value={resourceForm.description} onChange={handleResourceFormChange} rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label htmlFor="res-agency" className="block text-gray-700 text-sm font-bold mb-1">Agency</label>
                    <input id="res-agency" name="agency" value={resourceForm.agency} onChange={handleResourceFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label htmlFor="res-icon" className="block text-gray-700 text-sm font-bold mb-1">Icon (emoji)</label>
                    <input id="res-icon" name="icon" value={resourceForm.icon} onChange={handleResourceFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="res-url" className="block text-gray-700 text-sm font-bold mb-1">URL</label>
                    <input id="res-url" name="url" type="url" value={resourceForm.url} onChange={handleResourceFormChange} required placeholder="https://…"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label htmlFor="res-display-order" className="block text-gray-700 text-sm font-bold mb-1">Display Order</label>
                    <input id="res-display-order" name="display_order" type="number" value={resourceForm.display_order} onChange={handleResourceFormChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center">
                      <input id="res-is-official" name="is_official" type="checkbox" checked={resourceForm.is_official} onChange={handleResourceFormChange}
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 mr-2" />
                      <label htmlFor="res-is-official" className="text-gray-700 font-medium">Official</label>
                    </div>
                    <div className="flex items-center">
                      <input id="res-is-featured" name="is_featured" type="checkbox" checked={resourceForm.is_featured} onChange={handleResourceFormChange}
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 mr-2" />
                      <label htmlFor="res-is-featured" className="text-gray-700 font-medium">Featured</label>
                    </div>
                    <div className="flex items-center">
                      <input id="res-is-active" name="is_active" type="checkbox" checked={resourceForm.is_active} onChange={handleResourceFormChange}
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 mr-2" />
                      <label htmlFor="res-is-active" className="text-gray-700 font-medium">Active</label>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
                    {editingResourceId ? 'Save Changes' : 'Create Resource'}
                  </button>
                  <button type="button" onClick={cancelResourceForm} className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-semibold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Clicks</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Official</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Featured</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Active</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-navy-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredResources.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No resources match your filters.</td></tr>
                    ) : (
                      filteredResources.map((r) => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {r.title}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {categoryById.get(r.category_id)?.name || '—'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{r.click_count || 0}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => toggleResourceField(r, 'is_official')}
                              aria-label={`Toggle official status for ${r.title}`}
                              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${r.is_official ? 'bg-blue-100 text-navy-700' : 'bg-gray-100 text-gray-500'}`}
                            >
                              {r.is_official ? 'Yes' : 'No'}
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => toggleResourceField(r, 'is_featured')}
                              aria-label={`Toggle featured status for ${r.title}`}
                              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${r.is_featured ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-500'}`}
                            >
                              {r.is_featured ? 'Yes' : 'No'}
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => toggleResourceField(r, 'is_active')}
                              aria-label={`Toggle active status for ${r.title}`}
                              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                            >
                              {r.is_active ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-3">
                            <button
                              onClick={() => startEditResource(r)}
                              className="text-blue-700 hover:text-navy-700 font-semibold"
                            >
                              Edit
                            </button>
                            <button onClick={() => requestDelete('resource', r.id, r.title)} className="text-red-600 hover:text-red-900 font-semibold">
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- Analytics ---------------- */}
        {subTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
              <p className="text-sm font-medium text-blue-600">Total Clicks</p>
              <p className="text-3xl font-bold text-navy-700">{analytics.total_clicks || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-navy-700">Clicks by Resource</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-navy-700 uppercase tracking-wider">Click Count</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(analytics.resources || []).length === 0 ? (
                      <tr><td colSpan={2} className="px-6 py-8 text-center text-gray-500">No click data yet.</td></tr>
                    ) : (
                      analytics.resources.map((r) => (
                        <tr key={r.resource_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.title}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{r.click_count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Account Privacy & Data (GDPR/CCPA self-service) Component
// ─────────────────────────────────────────────────────────────

const CONSENT_TODAY_VERSION = () => new Date().toISOString().slice(0, 10);

const AccountPrivacy = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  // Consent state
  const [consentGranted, setConsentGranted] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [consentSuccess, setConsentSuccess] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);

  // Delete account state
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleted, setDeleted] = useState(false);

  const handleConsentChange = async (e) => {
    const granted = e.target.checked;
    setConsentSaving(true);
    setConsentError('');
    setConsentSuccess(false);

    try {
      await axios.post(`${API}/consent`, {
        consent_type: 'privacy_policy',
        version: CONSENT_TODAY_VERSION(),
        granted
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsentGranted(granted);
      setConsentSuccess(true);
      setTimeout(() => setConsentSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to record consent:', error);
      setConsentError(error.response?.data?.detail || 'Failed to record your consent. Please try again.');
    } finally {
      setConsentSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError('');
    setExportSuccess(false);

    try {
      const response = await axios.get(`${API}/me/data-export`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'my-immigration-services-data.json');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 4000);
    } catch (error) {
      console.error('Failed to export data:', error);
      setExportError(error.response?.data?.detail || 'Failed to download your data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;

    setDeleting(true);
    setDeleteError('');

    try {
      await axios.delete(`${API}/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDeleted(true);
      // Give the user a moment to see confirmation before we sign them out.
      setTimeout(async () => {
        await logout();
        navigate('/');
      }, 1800);
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleteError(error.response?.data?.detail || 'Failed to delete your account. Please try again.');
      setDeleting(false);
    }
  };

  if (deleted) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto text-center bg-white rounded-xl shadow-sm p-10 border border-slate-200">
          <div className="text-green-600 text-6xl mb-4" aria-hidden="true">✓</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Your account has been deleted</h2>
          <p className="text-gray-600">Signing you out...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center space-x-3">
          <div className="text-3xl" aria-hidden="true">🔒</div>
          <h2 className="text-3xl font-bold text-navy-700">Privacy & Data</h2>
        </div>

        {/* Privacy & Consent */}
        <section className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-navy-700 mb-2 flex items-center">
            <span className="mr-2" aria-hidden="true">📄</span>
            Privacy & Consent
          </h3>
          <p className="text-gray-600 mb-4">
            We take your privacy seriously. Please review our{' '}
            <a href="https://www.myimmiservices.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-semibold underline">
              Privacy Policy
            </a>{' '}
            to learn how we collect, use, and protect your data.
          </p>

          {consentError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
              {consentError}
            </div>
          )}

          {consentSuccess && (
            <div className="bg-green-50 border border-emerald-200 text-green-800 px-4 py-3 rounded-lg mb-4 font-semibold flex items-center">
              <span className="mr-2" aria-hidden="true">✅</span>
              Consent recorded
            </div>
          )}

          <div className="flex items-start space-x-3">
            <input
              id="privacy-consent-checkbox"
              type="checkbox"
              checked={consentGranted}
              onChange={handleConsentChange}
              disabled={consentSaving || consentGranted}
              className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
            <label htmlFor="privacy-consent-checkbox" className="text-gray-700 font-medium">
              I consent to the processing of my data as described in the Privacy Policy
              {consentSaving && <span className="ml-2 text-sm text-gray-500">Saving...</span>}
            </label>
          </div>
        </section>

        {/* Export your data */}
        <section className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-navy-700 mb-2 flex items-center">
            <span className="mr-2" aria-hidden="true">📦</span>
            Export Your Data
          </h3>
          <p className="text-gray-600 mb-4">
            Download a copy of everything My Immigration Services holds about you — your profile, cases, case
            history, legal marketplace listings, inquiries, notifications, and consent records — as a single JSON
            file.
          </p>

          {exportError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
              {exportError}
            </div>
          )}

          {exportSuccess && (
            <div className="bg-green-50 border border-emerald-200 text-green-800 px-4 py-3 rounded-lg mb-4 font-semibold flex items-center">
              <span className="mr-2" aria-hidden="true">✅</span>
              Your data has been downloaded
            </div>
          )}

          <button
            onClick={handleExport}
            disabled={exporting}
            className="bg-navy-700 hover:bg-navy-800 text-white py-3 px-6 rounded-lg transition-colors disabled:opacity-50 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            {exporting
              ? <><span aria-hidden="true">⏳</span> Preparing your data...</>
              : <><span aria-hidden="true">⬇️</span> Download my data</>}
          </button>
        </section>

        {/* Danger Zone: Delete Account */}
        <section className="bg-red-50 rounded-xl shadow-sm p-6 border border-red-200">
          <h3 className="text-xl font-bold text-red-800 mb-2 flex items-center">
            <span className="mr-2" aria-hidden="true">⚠️</span>
            Danger Zone: Delete Your Account
          </h3>
          <p className="text-red-900 mb-2 font-medium">
            Deleting your account permanently removes your profile, cases, case history, legal marketplace
            listings, inquiries, notifications, and consent records. This action cannot be undone.
          </p>
          <p className="text-red-900 mb-4 font-medium">
            Payment records will be anonymized (unlinked from your identity) but retained as required for legal
            and tax purposes.
          </p>

          {deleteError && (
            <div className="bg-white border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
              {deleteError}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="delete-account-confirm" className="block text-red-900 text-sm font-bold mb-2">
              Type DELETE to confirm you understand this action is permanent
            </label>
            <input
              id="delete-account-confirm"
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              placeholder="DELETE"
              autoComplete="off"
            />
          </div>

          <button
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText !== 'DELETE' || deleting}
            className="bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold focus:outline-none focus:ring-2 focus:ring-red-500/20"
          >
            {deleting
              ? <><span aria-hidden="true">⏳</span> Deleting your account...</>
              : <><span aria-hidden="true">🗑️</span> Permanently delete my account</>}
          </button>
        </section>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('cases');

  const NotificationsTab = () => <NotificationSettings user={user} token={token} />;

  const tabs = user?.is_admin ? [
    { id: 'cases', label: 'My Cases', component: CaseManager },
    { id: 'subscription', label: 'Subscription', component: () => <SubscriptionPlans /> },
    { id: 'notifications', label: 'Notifications', icon: '🔔', component: NotificationsTab },
    { id: 'listing', label: 'My Listing', icon: '⚖️', component: ProviderDashboard },
    { id: 'providers', label: 'Providers', icon: '⚖️', component: AdminProvidersTab },
    { id: 'resources', label: 'Resources', icon: '📚', component: AdminResourcesTab },
    { id: 'privacy', label: 'Privacy & Data', icon: '🔒', component: AccountPrivacy },
    { id: 'admin', label: 'Admin', icon: '👑', component: AdminDashboard }
  ] : [
    { id: 'cases', label: 'My Cases', component: CaseManager },
    { id: 'subscription', label: 'Subscription', component: () => <SubscriptionPlans /> },
    { id: 'notifications', label: 'Notifications', icon: '🔔', component: NotificationsTab },
    { id: 'listing', label: 'My Listing', icon: '⚖️', component: ProviderDashboard },
    { id: 'privacy', label: 'Privacy & Data', icon: '🔒', component: AccountPrivacy }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || CaseManager;

  return (
    <div className="min-h-screen bg-slate-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-navy-700 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        Skip to main content
      </a>
      <Header />

      <nav className="bg-white border-b border-slate-200" aria-label="Main navigation">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-3 font-semibold text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-slate-600 hover:text-navy-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {tab.icon && <span aria-hidden="true">{tab.icon}</span>}{tab.icon ? ' ' : ''}{tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main id="main-content">
        <ActiveComponent />
      </main>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Main App Component
function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/naturalization-test" element={<NaturalizationTest />} />
            <Route path="/find-legal-help" element={<ProviderSearch />} />
            <Route path="/find-legal-help/:id" element={<ProviderProfile />} />
            <Route path="/resources" element={<ResourcesCenter />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register onBack={() => window.location.href = '/login'} />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/payment-success" 
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment-cancel" 
              element={
                <ProtectedRoute>
                  <PaymentCancel />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;